import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { ref, computed } from 'vue'

// Telemetry event types for type safety
interface TelemetryEvent {
  name: string
  properties?: Record<string, string>
  measurements?: Record<string, number>
}

interface TelemetryPageView {
  name?: string
  uri?: string
  properties?: Record<string, string>
  measurements?: Record<string, number>
}

interface TelemetryException {
  exception: Error
  properties?: Record<string, string>
  measurements?: Record<string, number>
}

interface TelemetryDependency {
  name: string
  data: string
  duration: number
  success: boolean
  resultCode?: number
  properties?: Record<string, string>
  measurements?: Record<string, number>
}

interface TelemetryMetric {
  name: string
  average: number
  sampleCount?: number
  min?: number
  max?: number
  properties?: Record<string, string>
}

let appInsights: ApplicationInsights | null = null
const isInitialized = ref(false)
const initializationError = ref<string | null>(null)

export function useTelemetry() {
  const isEnabled = computed(() => {
    return !!(
      import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING ||
      import.meta.env.VITE_APP_INSIGHTS_INSTRUMENT_KEY
    )
  })

  const initialize = (): boolean => {
    try {
      // Skip if already initialized
      if (isInitialized.value && appInsights) {
        return true
      }

      // Skip if no configuration provided
      if (!isEnabled.value) {
        console.warn('Application Insights: No connection string or instrumentation key provided')
        return false
      }

      // Initialize Application Insights
      appInsights = new ApplicationInsights({
        config: {
          connectionString: import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING,
          instrumentationKey: import.meta.env.VITE_APP_INSIGHTS_INSTRUMENT_KEY,
          enableAutoRouteTracking: true, // Auto track SPA route changes
          enableCorsCorrelation: true, // Enable CORS correlation
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          autoTrackPageVisitTime: true,
          enableAjaxErrorStatusText: true,
          enableAjaxPerfTracking: true,
          disableExceptionTracking: false,
          disableTelemetry: false,
          loggingLevelConsole: import.meta.env.DEV ? 1 : 2, // More verbose in dev
          loggingLevelTelemetry: import.meta.env.DEV ? 1 : 2,
          samplingPercentage: import.meta.env.PROD ? 100 : 100, // 100% sampling for now
          isCookieUseDisabled: false,
        },
      })

      appInsights.loadAppInsights()

      // Add telemetry initializer to include app version in all telemetry
      appInsights.addTelemetryInitializer((envelope) => {
        try {
          // Get app version from global variable or fallback
          const appVersion = window.__APP_VERSION__ ?? __APP_VERSION__ ?? 'unknown'
          
          // Add app version to all telemetry items
          if (!envelope.data) {
            envelope.data = {}
          }
          
          // Add to custom properties based on telemetry type
          if (envelope.data.baseType === 'EventData') {
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          } else if (envelope.data.baseType === 'PageviewData') {
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          } else if (envelope.data.baseType === 'ExceptionData') {
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          } else if (envelope.data.baseType === 'RemoteDependencyData') {
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          } else if (envelope.data.baseType === 'MetricData') {
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          } else {
            // For any other telemetry types, add to custom properties
            envelope.data.baseData = envelope.data.baseData || {}
            envelope.data.baseData.properties = envelope.data.baseData.properties || {}
            envelope.data.baseData.properties.app_version = appVersion
          }
          
          // Also add to envelope tags for global context
          envelope.tags = envelope.tags || {}
          envelope.tags['ai.application.ver'] = appVersion
          
        } catch (error) {
          console.warn('Failed to add app version to telemetry:', error)
        }
        
        return true // Continue processing
      })

      isInitialized.value = true
      initializationError.value = null

      console.log('Application Insights initialized successfully')
      return true

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error'
      initializationError.value = errorMessage
      console.error('Failed to initialize Application Insights:', error)
      return false
    }
  }

  // Track custom events
  const trackEvent = (event: TelemetryEvent) => {
    if (!appInsights || !isInitialized.value) {
      console.warn('Application Insights not initialized, skipping event tracking')
      return
    }

    try {
      appInsights.trackEvent({
        name: event.name,
        properties: {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          ...event.properties,
        },
        measurements: event.measurements,
      })
    } catch (error) {
      console.error('Failed to track event:', error)
    }
  }

  // Track page views
  const trackPageView = (pageView?: TelemetryPageView) => {
    if (!appInsights || !isInitialized.value) {
      console.warn('Application Insights not initialized, skipping page view tracking')
      return
    }

    try {
      appInsights.trackPageView({
        name: pageView?.name || document.title,
        uri: pageView?.uri || window.location.href,
        properties: {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          ...pageView?.properties,
        },
        measurements: pageView?.measurements,
      })
    } catch (error) {
      console.error('Failed to track page view:', error)
    }
  }

  // Track exceptions
  const trackException = (exception: TelemetryException) => {
    if (!appInsights || !isInitialized.value) {
      console.warn('Application Insights not initialized, skipping exception tracking')
      return
    }

    try {
      appInsights.trackException({
        exception: exception.exception,
        properties: {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          url: window.location.href,
          userAgent: navigator.userAgent,
          ...exception.properties,
        },
        measurements: exception.measurements,
      })
    } catch (error) {
      console.error('Failed to track exception:', error)
    }
  }

  // Track dependencies (API calls, etc.)
  const trackDependency = (dependency: TelemetryDependency) => {
    if (!appInsights || !isInitialized.value) {
      console.warn('Application Insights not initialized, skipping dependency tracking')
      return
    }

    try {
      appInsights.trackDependencyData({
        id: `dep-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: dependency.name,
        data: dependency.data,
        duration: dependency.duration,
        success: dependency.success,
        responseCode: dependency.resultCode || 0,
        properties: {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          ...dependency.properties,
        },
        measurements: dependency.measurements,
      })
    } catch (error) {
      console.error('Failed to track dependency:', error)
    }
  }

  // Track custom metrics
  const trackMetric = (metric: TelemetryMetric) => {
    if (!appInsights || !isInitialized.value) {
      console.warn('Application Insights not initialized, skipping metric tracking')
      return
    }

    try {
      appInsights.trackMetric({
        name: metric.name,
        average: metric.average,
        sampleCount: metric.sampleCount,
        min: metric.min,
        max: metric.max,
        properties: {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          ...metric.properties,
        },
      })
    } catch (error) {
      console.error('Failed to track metric:', error)
    }
  }

  // Flush telemetry (useful for SPA navigation)
  const flush = () => {
    if (!appInsights || !isInitialized.value) return

    try {
      appInsights.flush()
    } catch (error) {
      console.error('Failed to flush telemetry:', error)
    }
  }

  return {
    // State
    isInitialized,
    isEnabled,
    initializationError,

    // Core methods
    initialize,
    flush,

    // Tracking methods
    trackEvent,
    trackPageView,
    trackException,
    trackDependency,
    trackMetric,
  }
}
